import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  Mail,
} from "lucide-react";
import { Brand } from "./Brand";
import { supabase } from "../lib/supabase";

type AuthView = "login" | "forgot" | "update-password";

type AuthScreenProps = {
  initialView?: "login" | "update-password";
  onRecoveryComplete?: () => void;
};

export function AuthScreen({ initialView = "login", onRecoveryComplete }: AuthScreenProps) {
  const [view, setView] = useState<AuthView>(initialView);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  useEffect(() => {
    if (initialView === "update-password") {
      setView("update-password");
      setError(null);
      setNotice(null);
    }
  }, [initialView]);

  const changeView = (nextView: AuthView) => {
    setView(nextView);
    setError(null);
    setNotice(null);
    setPassword("");
    setPasswordConfirmation("");
    setShowPassword(false);
    if (nextView !== "forgot") setResetEmailSent(false);
  };

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) setError("E-mail ou senha inválidos.");
    setLoading(false);
  };

  const submitResetRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase) return;

    setLoading(true);
    setError(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/`,
    });

    if (resetError) {
      setError("Não foi possível enviar o link agora. Aguarde um pouco e tente novamente.");
    } else {
      setResetEmailSent(true);
    }
    setLoading(false);
  };

  const submitNewPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase) return;

    setError(null);
    if (password.length < 8) {
      setError("A nova senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== passwordConfirmation) {
      setError("As senhas digitadas não são iguais.");
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError("Não foi possível salvar a nova senha. Solicite outro link de recuperação.");
      setLoading(false);
      return;
    }

    await supabase.auth.signOut();
    setPassword("");
    setPasswordConfirmation("");
    setView("login");
    setNotice("Senha criada com sucesso. Agora entre com seu e-mail e a nova senha.");
    setLoading(false);
    onRecoveryComplete?.();
  };

  const content = {
    login: {
      eyebrow: "Acesso restrito",
      title: "Entre no painel",
      description: "Use a conta cadastrada pela administração da equipe.",
    },
    forgot: {
      eyebrow: "Recuperação de acesso",
      title: resetEmailSent ? "Confira seu e-mail" : "Esqueceu sua senha?",
      description: resetEmailSent
        ? "Enviamos as instruções para criar uma nova senha."
        : "Informe seu e-mail para receber um link seguro de recuperação.",
    },
    "update-password": {
      eyebrow: "Novo acesso",
      title: "Crie uma nova senha",
      description: "Digite e confirme a senha que será usada para entrar no painel.",
    },
  }[view];

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <Brand />
        <div className="auth-card__intro">
          <span className="eyebrow">{content.eyebrow}</span>
          <h1>{content.title}</h1>
          <p>{content.description}</p>
        </div>

        {view === "login" && (
          <form onSubmit={submitLogin}>
            <label>
              E-mail
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <label>
              <span className="field-heading">
                <span>Senha</span>
                <button type="button" onClick={() => changeView("forgot")}>
                  Esqueci minha senha
                </button>
              </span>
              <span className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </span>
            </label>
            {notice && <p className="form-success"><CheckCircle2 size={16} />{notice}</p>}
            {error && <p className="form-error">{error}</p>}
            <button className="button button--primary auth-submit" disabled={loading}>
              {loading ? <LoaderCircle className="spin" size={18} /> : <LockKeyhole size={18} />}
              Entrar
            </button>
          </form>
        )}

        {view === "forgot" && !resetEmailSent && (
          <form onSubmit={submitResetRequest}>
            <label>
              E-mail
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                autoFocus
              />
            </label>
            {error && <p className="form-error">{error}</p>}
            <button className="button button--primary auth-submit" disabled={loading}>
              {loading ? <LoaderCircle className="spin" size={18} /> : <Mail size={18} />}
              Enviar link de recuperação
            </button>
            <button className="auth-back" type="button" onClick={() => changeView("login")}>
              <ArrowLeft size={16} /> Voltar para entrar
            </button>
          </form>
        )}

        {view === "forgot" && resetEmailSent && (
          <div className="auth-confirmation">
            <div className="auth-confirmation__icon"><Mail size={24} /></div>
            <p>
              Se existir uma conta para <strong>{email}</strong>, você receberá um link para criar a
              nova senha. Verifique também a pasta de spam.
            </p>
            <button className="button button--primary auth-submit" type="button" onClick={() => changeView("login")}>
              <ArrowLeft size={16} /> Voltar para entrar
            </button>
          </div>
        )}

        {view === "update-password" && (
          <form onSubmit={submitNewPassword}>
            <label>
              Nova senha
              <span className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </span>
            </label>
            <label>
              Confirmar nova senha
              <input
                type={showPassword ? "text" : "password"}
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
            </label>
            <p className="password-hint">Use pelo menos 8 caracteres.</p>
            {error && <p className="form-error">{error}</p>}
            <button className="button button--primary auth-submit" disabled={loading}>
              {loading ? <LoaderCircle className="spin" size={18} /> : <KeyRound size={18} />}
              Salvar nova senha
            </button>
            <button className="auth-back" type="button" onClick={() => changeView("forgot")}>
              Solicitar outro link de recuperação
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
